import {useCallback, useState} from 'react';
import {useSetAtom} from 'jotai';
import {usePathname, useRouter} from '@/hooks/useAppRouter';
import {productsAtom} from '@/store/products';
import {groupsSearchParamsAtom, getGroupsAtom} from '@/store/groups';
import {stylesSearchAtom, getStylesAtom} from '@/store/styles';
import {materialsSearchParamsAtom, getMaterialsAtom} from '@/store/materials';
import {getProducts} from '@/actions/products';
import {logger} from '@/lib/logger';
import type {LindaAction} from '../types';

const log = logger.tag('LindaActions');

export function useChatActions() {
    const [currentAction, setCurrentAction] = useState<string | null>(null);

    const router = useRouter();
    const pathname = usePathname();

    const setProductsState = useSetAtom(productsAtom);
    const setGroupsSearchParams = useSetAtom(groupsSearchParamsAtom);
    const setStylesSearch = useSetAtom(stylesSearchAtom);
    const executeGetGroups = useSetAtom(getGroupsAtom);
    const executeGetStyles = useSetAtom(getStylesAtom);
    const setMaterialsSearchParams = useSetAtom(materialsSearchParamsAtom);
    const executeGetMaterials = useSetAtom(getMaterialsAtom);

    const executeAction = useCallback(
        async (action: LindaAction) => {
            log.debug('Action type', {type: action.type});
            setCurrentAction(action.input.message ?? 'Performing action...');

            try {
                switch (action.type) {
                    case 'navigate': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const destination = action.input.destination;
                        const routes: Record<string, string> = {
                            dashboard: '/',
                            products: '/products',
                            groups: '/groups',
                            styles: '/styles',
                            materials: '/materials',
                            'modular-systems': '/modular-systems',
                            linda: '/linda',
                        };
                        const destinationStr =
                            typeof destination === 'string' ? destination : '';
                        router.push(routes[destinationStr] || '/');
                        break;
                    }

                    case 'search_products': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const query =
                            typeof action.input.query === 'string'
                                ? action.input.query
                                : '';
                        setProductsState((prev) => ({
                            ...prev,
                            query,
                            page: 0,
                        }));
                        setTimeout(() => getProducts(), 100);
                        if (!pathname.includes('/products')) {
                            router.push('/products');
                        }
                        break;
                    }

                    case 'search_groups': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const groupQuery =
                            typeof action.input.query === 'string'
                                ? action.input.query
                                : '';
                        setGroupsSearchParams((prev) => ({
                            ...prev,
                            name: groupQuery,
                            page: 1,
                        }));
                        setTimeout(() => executeGetGroups(), 100);
                        if (!pathname.includes('/groups')) {
                            router.push('/groups');
                        }
                        break;
                    }

                    case 'search_styles': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const styleQuery =
                            typeof action.input.query === 'string'
                                ? action.input.query
                                : '';
                        setStylesSearch((prev) => ({
                            ...prev,
                            query: styleQuery,
                            page: 0,
                        }));
                        setTimeout(() => executeGetStyles(), 100);
                        if (!pathname.includes('/styles')) {
                            router.push('/styles');
                        }
                        break;
                    }

                    case 'select_product': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const productPath = action.input.page
                            ? `/products/${action.input.productId}/${action.input.page}`
                            : `/products/${action.input.productId}`;
                        router.push(productPath);
                        break;
                    }

                    case 'select_group': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const groupPath = action.input.page
                            ? `/groups/${action.input.groupId}/${action.input.page}`
                            : `/groups/${action.input.groupId}`;
                        router.push(groupPath);
                        break;
                    }

                    case 'select_style': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const stylePath = action.input.page
                            ? `/styles/${action.input.styleId}/${action.input.page}`
                            : `/styles/${action.input.styleId}/style-details`;
                        router.push(stylePath);
                        break;
                    }

                    case 'select_material': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const materialPath = action.input.page
                            ? `/materials/${action.input.materialId}/${action.input.page}`
                            : `/materials/${action.input.materialId}`;
                        router.push(materialPath);
                        break;
                    }

                    case 'filter_products':
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        setProductsState((prev) => ({
                            ...prev,
                            ...(typeof action.input.status === 'string' && {
                                status: action.input.status,
                            }),
                            ...(typeof action.input.category === 'string' && {
                                category: action.input.category,
                            }),
                            ...(typeof action.input.subcategory ===
                                'string' && {
                                subcategory: action.input.subcategory,
                            }),
                            ...(typeof action.input.sortBy === 'string' && {
                                sortBy: action.input.sortBy as
                                    | 'name'
                                    | 'sku'
                                    | 'created_at',
                            }),
                            ...(typeof action.input.sortOrder === 'string' && {
                                sortOrder: action.input.sortOrder as
                                    | 'asc'
                                    | 'desc',
                            }),
                            page: 0,
                        }));
                        setTimeout(() => getProducts(), 100);
                        if (!pathname.includes('/products')) {
                            router.push('/products');
                        }
                        break;

                    case 'filter_styles':
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        setStylesSearch((prev) => ({
                            ...prev,
                            filter: {
                                ...prev.filter,
                                ...(typeof action.input.roomType ===
                                    'string' && {
                                    roomType: action.input.roomType,
                                }),
                                ...(Array.isArray(action.input.tags) && {
                                    tags: action.input.tags,
                                }),
                                ...(typeof action.input.category ===
                                    'string' && {
                                    category: action.input.category,
                                }),
                            },
                            page: 0,
                        }));
                        setTimeout(() => executeGetStyles(), 100);
                        if (!pathname.includes('/styles')) {
                            router.push('/styles');
                        }
                        break;

                    case 'filter_groups':
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        setGroupsSearchParams((prev) => ({
                            ...prev,
                            ...(typeof action.input.state === 'string' && {
                                state: action.input.state as
                                    | ''
                                    | 'hidden'
                                    | 'shared',
                            }),
                            ...(action.input.sortOrder === 'desc' && {
                                sort_order: 'desc' as const,
                            }),
                            page: 1,
                        }));
                        setTimeout(() => executeGetGroups(), 100);
                        if (!pathname.includes('/groups')) {
                            router.push('/groups');
                        }
                        break;

                    case 'search_materials': {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        const materialQuery =
                            typeof action.input.query === 'string'
                                ? action.input.query
                                : '';
                        setMaterialsSearchParams((prev) => ({
                            ...prev,
                            q: materialQuery,
                            page: 1,
                        }));
                        setTimeout(() => executeGetMaterials(), 100);
                        if (!pathname.includes('/materials')) {
                            router.push('/materials');
                        }
                        break;
                    }

                    case 'filter_materials':
                        await new Promise((resolve) =>
                            setTimeout(resolve, 300)
                        );
                        setMaterialsSearchParams((prev) => ({
                            ...prev,
                            ...(typeof action.input.kind === 'string' && {
                                kind: action.input.kind,
                            }),
                            ...(typeof action.input.source === 'string' && {
                                source: action.input.source as
                                    | 'static'
                                    | 'dynamic',
                            }),
                            ...(typeof action.input.sortBy === 'string' && {
                                sort_by: action.input.sortBy as
                                    | 'name'
                                    | 'sku'
                                    | 'created_at'
                                    | 'updated_at',
                            }),
                            ...(typeof action.input.sortOrder === 'string' && {
                                sort_order: action.input.sortOrder as
                                    | 'asc'
                                    | 'desc',
                            }),
                            page: 1,
                        }));
                        setTimeout(() => executeGetMaterials(), 100);
                        if (!pathname.includes('/materials')) {
                            router.push('/materials');
                        }
                        break;

                    default:
                        log.warn('Unknown action type', {type: action.type});
                }
            } catch (error) {
                log.error('Error executing action', error);
            } finally {
                setCurrentAction(null);
            }
        },
        [
            router,
            pathname,
            setProductsState,
            setGroupsSearchParams,
            setStylesSearch,
            executeGetGroups,
            executeGetStyles,
            setMaterialsSearchParams,
            executeGetMaterials,
        ]
    );

    return {
        currentAction,
        executeAction,
    };
}
